import SearchApi from '../../api/Search'
import AppConfig from "@/classes/AppConfig";
import Lytics from '../../classes/Lytics';
import ObjPropParser from "@/classes/ObjPropParser";
import SearchCTCPersonalizationApi from "../../api/SearchCTCPersonalizationApi";

export const SEARCH_MODE_WITH_POSITIONS = 'searchwithposition';
export const SEARCH_MODE_DEFAULT = 'search';


const state = {
  query: "",
  showMobileSearchBar: false,
  showSubHeader: false,
  filteredItems: [],
  showSearchResults: false,
  saving: null,
  pricing: null,
  loyalty:null,
  sort: null,          // active sort value: 'price_asc' | 'price_desc' | 'rating_desc' | 'rating_asc' | null
  rating: null,        // active rating filter: '3' | '4' | '5' | null — keeps products >= this star value
  BrandDescriptions:[],
  availableBrandOptions: [],
  brandOptionsCache: {},
  categories: [],
  subCategories: [],
  searchClient: null,
  loading: false,
  hits: [],
  rawHits: [],       // unordered hits from last API call; sortedHits + ratedHits getter reads this
  rawHitsFilters: null, // snapshot of filters used for the cached rawHits; null = no cache
  results: null,
  activeSearchRequestId: 0,
  // ai search
  chatConfig: null,
  isChatbotOpen: false,
  response: '',
  resultsLoading: false,
  messages: [],
  aiSuggestions: [],
  error: null
};

const getters = {
  filteredItems(state, getters) {
    return (getters.searchMode === SEARCH_MODE_WITH_POSITIONS? state.results?.page?.blocks: state.results) || [];
  },

  // Reactive getter: re-sorts rawHits whenever state.sort or state.rawHits changes.
  // Changing the sort option updates state.sort via SET_SORT — no API call needed.
  sortedHits(state) {
    return applySortToHits(state.rawHits, state.sort);
  },
  ratedHits(state) {
    const hits = state.rawHits;
    if (state.rating) {
      const minRating = parseFloat(state.rating);
      return hits.filter(h => (parseFloat(h.rating?.averageRating) || 0) >= minRating);
    }
    return hits;
  },

  availableRatingThresholds(state) {
    return [1, 2, 3, 4, 5].filter(threshold =>
      state.rawHits.some(h => {
        const r = parseFloat(h.rating?.averageRating) || 0;
        return threshold < 5 ? (r >= threshold && r < threshold + 1) : r >= 5;
      })
    );
  },
   availableBrands(state) {
    return state.availableBrandOptions;
},

  showSearchResults(state) {
    return state.showSearchResults
  },
  config(state, getters, rootState, rootGetters) {
    const config = rootGetters["config/features"].search;
    //console.log('Root Getters:', rootGetters);
    return ObjPropParser.Parse(config || {}, {__self:rootGetters});
  },

  searchMode(state, getters) {
    return getters.config.mode || SEARCH_MODE_DEFAULT;
  },

  searchResults(state) {
    return state.results
  },

  responseContent(state) {
    const match = state.response.match(/"response_content":\s*"([^"]*)"*/);

    if (match) {
      return match[1];
    }
  },

  relevantProducts(state) {
    const match = state.response.match(/"sku":\s*"([^"]*)"/g);
    if (!match) return [];
    return match.map(sku => sku.match(/"sku":\s*"([^"]*)"/)[1]);
  },

  relevantProductsFull(state, getters, rootState, rootGetters) {
    return getters.relevantProducts.map((sku) =>
      rootGetters.productsBySKU[sku]
    );
  }
};
const normalizeArray = (value) =>
  Array.isArray(value) ? [...value].sort() : [];

const getBrandOptionsCacheKey = (payload, pricing, lockToCategoryScope) => {
  const base = {
    env: payload.env,
    title: payload.title,
    storeId: payload.storeId,
    lang: payload.lang,
    category: normalizeArray(payload.category),
    subCategory: normalizeArray(payload.subCategory),
    text: payload.text || "",
    loyalty: payload.loyalty || ""
  };
  if (lockToCategoryScope) return JSON.stringify(base);
  return JSON.stringify({ ...base, saving: payload.saving || 0, pricing: pricing || null });
};

const getBrandsFromHits = hits => [
  ...new Set(hits.filter(item => item.brand?.name).map(item => item.brand.name))
];

function applyPricingFilter(hits, pricing) {
  if (!pricing || !Array.isArray(pricing)) return hits;
  const [min, max] = pricing;
  return hits.filter(p => {
    const price = parseFloat(p.pricing?.currentPrice?.value);
    if (isNaN(price)) return false;
    if (max != null) return price >= min && price <= max;
    return price >= min;
  });
}

function applySortToHits(hits, sort) {
  if (sort === 'price_asc') return [...hits].sort((a, b) => (parseFloat(a.pricing?.currentPrice?.value) || 0) - (parseFloat(b.pricing?.currentPrice?.value) || 0));
  if (sort === 'price_desc') return [...hits].sort((a, b) => (parseFloat(b.pricing?.currentPrice?.value) || 0) - (parseFloat(a.pricing?.currentPrice?.value) || 0));
  if (sort === 'rating_desc') return [...hits].sort((a, b) => (parseFloat(b.rating?.averageRating) || 0) - (parseFloat(a.rating?.averageRating) || 0));
  if (sort === 'rating_asc') return [...hits].sort((a, b) => (parseFloat(a.rating?.averageRating) || 0) - (parseFloat(b.rating?.averageRating) || 0));
  return hits;
}

function commitResetSearchState(commit) {
  commit("SHOW_SEARCH_RESULTS", false);
  commit("CLEAR_HITS");
  commit("UPDATE_SEARCH_RESULTS", null);
  commit("SET_SAVING", null);
  commit("SET_PRICING", null);
  commit("SET_CATEGORIES", []);
  commit("SET_SUBCATEGORIES", []);
  commit("SET_BRAND_DESCRIPTIONS", []);
  commit("SET_LOYALTY", null);
  commit("SET_SORT", null);
  commit("SET_RATING", null);
  commit("CLEAR_BRAND_OPTIONS_CACHE");
}

async function resolveAvailableBrandOptions(state, payload, features) {
  if (!state.BrandDescriptions.length) {
    return getBrandsFromHits(applyPricingFilter(payload._mainResponseProducts, state.pricing));
  }

  const brandOptionCalls = [];

  if (state.categories.length) {
    brandOptionCalls.push(
      SearchCTCPersonalizationApi.search(
        { ...payload, category: state.categories, subCategory: [], BrandDescriptions: [] },
        features
      )
    );
  }

  if (state.subCategories.length) {
    brandOptionCalls.push(
      SearchCTCPersonalizationApi.search(
        { ...payload, category: [], subCategory: state.subCategories, BrandDescriptions: [] },
        features
      )
    );
  }

  if (!state.categories.length && !state.subCategories.length) {
    brandOptionCalls.push(
      SearchCTCPersonalizationApi.search(
        { ...payload, BrandDescriptions: [] },
        features
      )
    );
  }

  const responses = await Promise.all(brandOptionCalls);
  return [...new Set(responses.flatMap(r => getBrandsFromHits(applyPricingFilter(r?.data?.ctcProducts || [], state.pricing))))];
}
const actions = {
  setChatbot({ commit }, value) {
    commit("SET_CHATBOT", value);
  },
 
  async queryAlgolia({ state, rootGetters, dispatch, commit }, query) {
    if (!rootGetters["config/features"].enhancedSearch) return;
  const requestId = Date.now();
  commit("SET_ACTIVE_SEARCH_REQUEST_ID", requestId);
  //it is added to have smooth UI switching between filter options
  const shouldShowGlobalLoader = !rootGetters['inlineDrawers/isFilterSortWindowOpen'];

    //const banner = AppConfig?.AppConf?.algolia_id;
    const lang = rootGetters.language;
   // const indexName = `products_${lang}_${banner}`;
    //const index = state.searchClient.initIndex(indexName);

    if (query && query !== state.query) dispatch("updateSearchQuery", query);

    //let filters = `storeList:${rootGetters.storeId} AND title:${rootGetters.dealId} AND env:Production`;
    let filters="";
    //CTCCore Production
    if (state.categories.length)
      filters += ` AND (${state.categories
        .map((cat) => `mainCategory${lang === "en" ? "En" : "Fr"}:"${cat}"`)
        .join(" OR ")})`;
    if (state.subCategories.length)
      filters += ` AND (${state.subCategories
        .map(
          (subCat) => `subCategory${lang === "en" ? "En" : "Fr"}:"${subCat}"`
        )
        .join(" OR ")})`;
        if (state.BrandDescriptions.length) {
           filters += ` AND (${state.BrandDescriptions
             .map((brand) => `brandDescription:"${brand}"`)
           .join(" OR ")})`;
        }
    if (state.saving) filters += ` AND saveInt>=${state.saving}`;
    if (state.loyalty) filters += ` AND loyalty:"${state.loyalty}"`;  
    if (shouldShowGlobalLoader) {commit("UPDATE_LOADING", 1, { root: true });}
    try {
        dispatch('setLoadingState', true);
        //commit('CLEAR_HITS');
        //  commit("UPDATE_LOADING", 1, { root: true });
      console.log("filter",filters)
      //const { hits } = await index.search(query || state.query, { filters });
      const payload = {
        env: "Production",
        title: String(rootGetters.dealId),     
        storeId: String(rootGetters.storeId), 
        lang: rootGetters.language || "en",
        category: state.categories || [],
        subCategory: state.subCategories || [],
        saving: Number(state.saving) || 0,
        text: state.query || "",
        BrandDescriptions: state.BrandDescriptions || [],
        loyalty: state.loyalty || "" 
      };

       const response  = await SearchCTCPersonalizationApi.search(payload,rootGetters["config/features"]);
      ///Brand
      const isOnlyBrandSelected =
        state.BrandDescriptions.length > 0 &&
        !state.categories.length &&
        !state.subCategories.length &&
        !state.saving &&
        !state.pricing &&
        !state.loyalty &&
        !(state.query || "").trim();

      let availableBrandOptions;

      if (isOnlyBrandSelected) {
        // Brand is the first/only filter — reuse the brand list already built without a brand filter.
        // No second API call needed; just ensure the selected brand stays visible.
        // const base = state.availableBrandOptions.length
        //   ? state.availableBrandOptions
        //   : getBrandsFromHits(response?.data?.ctcProducts || []);
        // availableBrandOptions = [...new Set([...base, ...state.BrandDescriptions])];
        availableBrandOptions = state.availableBrandOptions;
      } else {
        const lockToCategoryScope =
          state.categories.length > 0 || state.subCategories.length > 0 || !!state.loyalty;
        const brandCacheKey = getBrandOptionsCacheKey(payload, state.pricing, lockToCategoryScope);
        availableBrandOptions = state.brandOptionsCache[brandCacheKey];
        if (!availableBrandOptions) {
          availableBrandOptions = await resolveAvailableBrandOptions(
            state,
            { ...payload, _mainResponseProducts: response?.data?.ctcProducts || [] },
            rootGetters["config/features"]
          );
          commit("SET_BRAND_OPTIONS_CACHE", { key: brandCacheKey, brands: availableBrandOptions });
        }
        // Keep selected brand visible even if it produced empty results alongside other filters
        if (state.BrandDescriptions.length && !state.categories.length && !state.subCategories.length) {
          availableBrandOptions = [...new Set([...availableBrandOptions, ...state.BrandDescriptions])];
        }
      }

      commit("SET_AVAILABLE_BRAND_OPTIONS", availableBrandOptions);
      ///End of brand
        if (state.activeSearchRequestId !== requestId) return;
        const hits = response?.data?.ctcProducts || [];
        console.log("hits", hits)

          const finalHits = applyPricingFilter(hits, state.pricing);

    // Store unsorted hits as the Vuex cache — sort changes reuse this without an API call
        commit("SET_RAW_HITS", finalHits);
        commit("SET_RAW_HITS_FILTERS", {
          query: state.query || "",
          categories: normalizeArray(state.categories),
          subCategories: normalizeArray(state.subCategories),
          BrandDescriptions: normalizeArray(state.BrandDescriptions),
          saving: state.saving ?? null,
          loyalty: state.loyalty ?? null,
          pricing: state.pricing ?? null
        });

    let displayHits = applySortToHits(finalHits, state.sort);
    if (state.rating) {
      const minRating = parseFloat(state.rating);
      displayHits = displayHits.filter(h => (parseFloat(h.rating?.averageRating) || 0) >= minRating);
    }
    dispatch("setHits", displayHits);
    console.log("Algolia search results:", displayHits);
    Lytics.addToEventQueue({
      item_list_name: "search",
      personalized_offers: rootGetters['ctUser/triangleOffers'] || [],
      items: finalHits.map((p, i) => ({
        product_id: p.sku,
        name: p.productEn || p.productFr,
        name_en: p.productEn,
        name_fr: p.productFr,
        price: p.regularPrice,
        position: i,
        sale_price: p.salePrice,
        category: p.mainCategoryEn || p.mainCategoryFr,
        category_2: p.subCategoryEn || p.subCategoryFr,
        image_url: p.images?.[0]?.url,
        quantity: 1,
      })),
    });
     
    } catch (e) {
      if (state.activeSearchRequestId !== requestId) return;
      if (e?.code !== 'ERR_CANCELED') {
        dispatch("setLoadingState", false);
        console.error(e);
      }
    } finally {
       if (state.activeSearchRequestId === requestId) {
      dispatch("setLoadingState", false);
    }
      if (shouldShowGlobalLoader) {
    commit("UPDATE_LOADING", -1, { root: true });
  }
     // commit("UPDATE_LOADING", -1, { root: true });
    }
  },

  setHits({ commit }, hits) {
    commit("SET_HITS", hits);
  },


async filterProducts({ commit, rootGetters, rootState, dispatch, getters, state }, filterQuery) {
  const currentFlyer = rootGetters["landingPage/currentFlyer"];

  if (rootGetters["inlineDrawers/isFilterSortWindowOpen"]) {
    return;
  }

  const { Sort, rating: ratingFilter, ...otherFilters } = filterQuery;
  const showResults = !!Object.keys(otherFilters).length;
  const enhancedSearch = rootGetters["config/features"].enhancedSearch;

  const incomingFilters = {
    query: state.query || "",
    categories: normalizeArray(otherFilters.category),
    subCategories: normalizeArray(otherFilters.subCategory),
    BrandDescriptions: normalizeArray(otherFilters.BrandDescriptions),
    saving: otherFilters.saving ?? null,
    loyalty: otherFilters.loyalty?.[0] ?? null,
    pricing: otherFilters.pricing ?? null
  };

  const nonSortRatingFiltersUnchanged =
    state.rawHits.length > 0 &&
    state.showSearchResults &&
    state.rawHitsFilters !== null &&
    JSON.stringify(incomingFilters) === JSON.stringify(state.rawHitsFilters);

  if (nonSortRatingFiltersUnchanged) {
    commit("SET_SORT", Sort || null);
    commit("SET_RATING", ratingFilter || null);

    let sorted = [...state.rawHits];

    if (ratingFilter) {
      const minRating = parseFloat(ratingFilter);
      sorted = sorted.filter(h => {
        const avg = parseFloat(h.rating?.averageRating);
        return !isNaN(avg) && avg >= minRating;
      });
    }

    sorted = applySortToHits(sorted, Sort);

    dispatch("setHits", sorted);
    return;
  }

  let searchResult = [];

  if (!showResults) {
    commitResetSearchState(commit);
    dispatch("filters/clearSort", null, { root: true });
    dispatch("filters/clearRating", null, { root: true });
    dispatch("filters/clearAccordionSections", null, { root: true });
    dispatch("setTiledView", false, { root: true });
    return;
  }

  if (!enhancedSearch) {
    try {
      searchResult = await SearchApi.Post(
        currentFlyer.title,
        rootState.currentStore,
        rootGetters.language,
        filterQuery,
        rootGetters.highlightResults,
        getters.searchMode
      );
    } catch (e) {
      searchResult = [];
    }

    commit("UPDATE_SEARCH_RESULTS", searchResult);

    const filteredItems = getters.filteredItems;

    const products = filteredItems.reduce((acc, item) => {
      return [...acc, ...item.products];
    }, []);

    Lytics.addToEventQueue({
      item_list_name: "search",
      personalized_offers: rootGetters["ctUser/triangleOffers"] || [],
      items: products.map((p, i) => {
        return {
          product_id: p.sku,
          name: p.productEn || p.productFr,
          name_en: p.productEn,
          name_fr: p.productFr,
          price: p.regularPrice,
          position: i,
          sale_price: p.salePrice,
          category: p.mainCategoryEn || p.mainCategoryFr,
          category_2: p.subCategoryEn || p.subCategoryFr,
          image_url: p.productImage,
          quantity: 1,
        };
      }),
    });
  }

  commit("SHOW_SEARCH_RESULTS", true);

  filterQuery.saving ? commit("SET_SAVING", filterQuery.saving) : commit("SET_SAVING", null);
  filterQuery.category ? commit("SET_CATEGORIES", filterQuery.category) : commit("SET_CATEGORIES", []);
  filterQuery.subCategory ? commit("SET_SUBCATEGORIES", filterQuery.subCategory) : commit("SET_SUBCATEGORIES", []);
  filterQuery.pricing ? commit("SET_PRICING", filterQuery.pricing) : commit("SET_PRICING", null);
  filterQuery.BrandDescriptions ? commit("SET_BRAND_DESCRIPTIONS", filterQuery.BrandDescriptions) : commit("SET_BRAND_DESCRIPTIONS", []);
  filterQuery.loyalty?.length ? commit("SET_LOYALTY", filterQuery.loyalty[0]) : commit("SET_LOYALTY", null);
  filterQuery.Sort ? commit("SET_SORT", filterQuery.Sort) : commit("SET_SORT", null);
  filterQuery.rating ? commit("SET_RATING", filterQuery.rating) : commit("SET_RATING", null);

  if (enhancedSearch) {
    dispatch("queryAlgolia");
  }
},
  async updateSearchQuery({ commit }, query) {
    commit("UPDATE_SEARCH_QUERY", query);
  },

  async fetchAIConfig({ commit }) {
    try {
      const response = await fetch(
        `${process.env.VUE_APP_CHATBOT_API_URL}/config?code=r6GwVSiiAj9FRXl10XK9NiJ_lggBD3FSctIt8Hbq4l2QAzFuEs7Ytg==`,
        {
          method: "POST",
          body: JSON.stringify({
            banner: AppConfig?.AppConf?.banner_id || process.env.VUE_APP_BANNER,
          }),
        }
      );
      const data = await response.json();
      commit("SET_AI_CONFIG", data);
    } catch (error) {
      console.error("error", error);
    }
  },

  async fetchAISuggestions({ commit, rootGetters }) {
    console.log('fetching ai suggestions with language:', rootGetters.language);
    try {
      const response = await fetch(
        `${process.env.VUE_APP_CHATBOT_API_URL}/suggestions?code=lRhn5skrJTBVkTFts7Gdy_BqKmw5iZswN-x0oKnYEX3rAzFucjM8QA==`,
        {
          method: "POST",
          body: JSON.stringify({
            banner: AppConfig?.AppConf?.banner_id || process.env.VUE_APP_BANNER,
            language: rootGetters.language,
          }),
        }
      );
      const data = await response.json();

      commit("SET_AI_SUGGESTIONS", data);
    } catch (error) {
      console.error("error", error);
    }
  },

  // eslint-disable-next-line
  async queryAISearch({ commit, state, getters, rootState }, query) {
    commit("APPDEND_MESSAGE", {
      role: "user",
      content: query,
      display_content: query,
    });

    const raw = JSON.stringify({
      messages: state.messages,
      page_json_url: `${AppConfig?.AppConf?.api ||
        process.env.VUE_APP_API_URL}/pages/${rootState.landingPage.flyerId}/${
        rootState.currentStore
      }/en/`,
      subscription_key:
        AppConfig?.AppConf?.apikey || process.env.VUE_APP_API_KEY,
      banner: AppConfig?.AppConf?.banner_id || process.env.VUE_APP_BANNER
    });

    const requestOptions = {
      method: "POST",
      body: raw,
      redirect: "follow",
    };

    try {
      commit("SET_RESULTS_LOADING", true);
      commit("CLEAR_ERROR");
      const response = await fetch(
        `${process.env.VUE_APP_CHATBOT_API_URL}/chat?code=punwY0tsUIJrqA6Eb6VzBDDvBJAE9TIM3k9QbRzrqq2fAzFujtwIQQ==`,
        requestOptions
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let partialChunk = "";

      // eslint-disable-next-line
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        let chunk = decoder.decode(value, { stream: true });
        if (chunk.includes("data:")) {

          chunk = chunk.replace(/data:/, "");
          chunk = chunk.replace(/(\r\n|\n|\r)/gm, "");


          // Decode the streamed text data
          partialChunk += chunk;

          // Process the text data incrementally (for example, append it to the response)
          commit("SET_AI_SEARCH_RESPONSE", partialChunk);
        }
      }

      commit("APPDEND_MESSAGE", {
        role: "assistant",
        content: state.response,
        display_content: getters.responseContent,
        display_products: getters.relevantProductsFull,
        display_flyer_id: rootState.landingPage.flyerId,
      });
      commit("SET_AI_SEARCH_RESPONSE", "");
      commit("SET_RESULTS_LOADING", false);
    } catch (error) {
      commit("SET_AI_SEARCH_RESPONSE", "");
      commit("SET_RESULTS_LOADING", false);
      commit("SET_ERROR", error);
      console.error("error", error);
    }
  },

  initChatbot({ dispatch }) {
    dispatch("fetchAIConfig");
    dispatch("fetchAISuggestions");
  },

  setSubHeaderState({ commit }, val) {
    commit("SET_SUB_HEADER_STATE", val);
  },

  toggleMobileSearchBar({ commit }) {
    commit("TOGGLE_MOBILE_SEARCH_BAR");
  },

  closeMobileSearchBar({ commit }) {
    commit("CLOSE_MOBILE_SEARCH_BAR");
  },

  setShowSearchResults({ commit }, val) {
    commit("SHOW_SEARCH_RESULTS", val);
  },

  resetQuery({ commit,dispatch }) {
    commit("UPDATE_SEARCH_QUERY", "");
    commit("SHOW_SEARCH_RESULTS", false);
    dispatch("setTiledView", false, { root: true });

  },

  resetFilters({ commit,dispatch }) {
    console.log("Reset Filters");
    commit("UPDATE_SEARCH_QUERY", "");
    commitResetSearchState(commit);
    dispatch("setTiledView", false, { root: true });
    commit("CLEAR_AVAILABLE_BRAND_OPTIONS");
  },

  setSearchClient({ commit }, val) {
    commit("SET_SEARCH_CLIENT", val);
  },

  setLoadingState({ commit }, val) {
    commit("SET_LOADING", val);
  }
};

const mutations = {
  SET_CHATBOT(state, value) {
    state.isChatbotOpen = value;
  },

  SET_AI_CONFIG(state, config) {
    state.chatConfig = config;
  },
SET_ACTIVE_SEARCH_REQUEST_ID(state, id) {
  state.activeSearchRequestId = id;
},
  SET_AI_SUGGESTIONS(state, suggestions) {
    state.aiSuggestions = suggestions;
  },

  APPDEND_MESSAGE(state, message) {
    state.messages.push(message);
  },

  SET_RESULTS_LOADING(state, value) {
    state.resultsLoading = value;
  },

  SET_AI_SEARCH_RESPONSE(state, response) {
    state.response = response;
  },

  UPDATE_SEARCH_QUERY(state, query) {
    state.query = query;
  },

  UPDATE_SEARCH_RESULTS(state, results) {
    if(results?.page?.id === null) {
      results.page.id = "Search Result"
    }
    state.results = results;
  },

  UPDATE_FILTERED_ITEMS(state, filteredItems) {
    state.filteredItems = filteredItems
  },

  SHOW_SEARCH_RESULTS(state, value) {
    state.showSearchResults = value
  },

  TOGGLE_MOBILE_SEARCH_BAR(state) {
    state.showMobileSearchBar = !state.showMobileSearchBar;
  },

  CLOSE_MOBILE_SEARCH_BAR(state) {
    state.showMobileSearchBar = false;
  },

  SET_SUB_HEADER_STATE(state, val) {
    state.showSubHeader = val;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  SET_SAVING(state, val) {
    state.saving = val;
  },
  SET_PRICING(state, val) {
    state.pricing = val;
  },
  SET_CATEGORIES(state, val) {
    state.categories = val;
  },

  SET_SUBCATEGORIES(state, val) {
    state.subCategories = val;
  },
  SET_BRAND_DESCRIPTIONS(state, val) {
    state.BrandDescriptions = val;
  },
  SET_AVAILABLE_BRAND_OPTIONS(state, brands) {
    state.availableBrandOptions = brands;
  },
  SET_BRAND_OPTIONS_CACHE(state, { key, brands }) {
    state.brandOptionsCache = { ...state.brandOptionsCache, [key]: brands };
  },
  CLEAR_BRAND_OPTIONS_CACHE(state) {
    state.brandOptionsCache = {};
  },
  SET_LOYALTY(state, val) {
    state.loyalty = val;
  },
  SET_SORT(state, val) {
    state.sort = val;
  },
  SET_RATING(state,val)
  {
  state.rating = val;
  },
  SET_SEARCH_CLIENT(state, val) {
    state.searchClient = val;
  },

  CLEAR_HITS(state) {
    state.hits = [];
    state.rawHits = [];
    state.rawHitsFilters = null;
    state.availableBrandOptions = [];
    state.brandOptionsCache = {};
  },
CLEAR_AVAILABLE_BRAND_OPTIONS(state) {
  state.availableBrandOptions = [];
},
  SET_HITS(state, hits) {
    state.hits = hits;
    state.loading = false;
  },

  // Stores unordered hits from the API. sortedHits getter reads this to apply sort client-side.
  SET_RAW_HITS(state, hits) {
    state.rawHits = hits;
  },

  
  SET_RAW_HITS_FILTERS(state, filters) {
    state.rawHitsFilters = filters;
  },

  SET_LOADING(state, val) {
    state.loading = val;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
